import { CheckOutlined, CheckSquareFilled, CloseOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Input, Modal } from 'antd';
import { useEffect, useState } from 'react';
import FlagPriority from '../../assets/icons/flag-priority.svg?react';
import FlagPriorityWhite from '../../assets/icons/FlagPriorityWhite.svg?react';
import { capitalizeFirstLetter } from '../../common/utils';
import colorTokens from '../../constants/design_system/colors';
import { Epic } from '../../types/Epic';
import { SpaceLabel } from '../../types/TaskLabel';
import { TaskStatus, TaskStatusGroup, TaskStatusGroupUtils } from '../../types/TaskStatus';
import { TaskType } from '../../types/TaskType';
import { User } from '../../types/User';
import { flagWidth, PriorityFlagPropsMap, PriorityOptions, UserAvatar } from '../TaskTable/helpers/helper';
import { BaseButton } from '../UI/BaseButton';
import { TaskTypeLabel } from '../UI/Dropdown/TaskTypeDropdown';
import LabelChip, { LabelChipType } from '../UI/LabelChip';
import { SprintStatusLabel } from '../UI/SprintStatusLabel';
import StatusLabel, { StatusComponentType } from '../UI/StatusLabel';
import { ConfirmSaveForEveryoneModal } from './ConfirmSaveForEveryoneModal';

interface TaskFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedFilters: Filter[]) => void;
  resetToSavedFilters: () => void;
  saveFiltersForAll: () => void;
  filters: Filter[];
  viewSelectedFilters: Filter[];
  isViewSelectedFilters: boolean;
}

export interface Filter {
  type:
    | 'taskType'
    | 'sprintStatus'
    | 'epic'
    | 'owner'
    | 'status'
    | 'deadline'
    | 'startDate'
    | 'priority'
    | 'estimate'
    | 'labels';
  name: string;
  isMultiselect: boolean;
  isSearchable: boolean;
  options: FilterOption[];
}

const TaskFiltersModal = (props: TaskFiltersModalProps) => {
  const [filters, setFilters] = useState<Filter[]>(props.filters);
  const [selectedFilter, setSelectedFilter] = useState<Filter>(filters[0]);
  const [searchValue, setSearchValue] = useState('');
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  // Sync local state with props when they change
  useEffect(() => {
    setFilters(props.filters);
    if (props.filters.length > 0) {
      setSelectedFilter(props.filters.find((filter) => filter.type === selectedFilter.type) || props.filters[0]);
    }
  }, [props.filters]);

  const groupedFilterOptions = groupFilterOptions(selectedFilter);
  const selectedFilters = filters.filter((filter) => {
    const selectedOptions = filter.options.filter((option) => option.selected);
    return selectedOptions.length > 0;
  });
  const selectedFilterSearchedOptions = selectedFilter.options.filter(
    (option) => option.name && option.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const onSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleCancel = () => {
    props.onClose();
  };

  const handleSaveForAllClick = () => {
    setIsConfirmModalOpen(true);
  };

  const handleConfirmSave = () => {
    setIsConfirmModalOpen(false);
    props.saveFiltersForAll();
  };

  const handleCancelConfirm = () => {
    setIsConfirmModalOpen(false);
  };

  const clearAllFilters = () => {
    const updatedFilters = filters.map((filter) => {
      const updatedOptions = filter.options.map((option) => {
        return { ...option, selected: false };
      });
      return { ...filter, options: updatedOptions };
    });

    const updatedSelectedOptions = selectedFilter.options.map((option) => {
      return { ...option, selected: false };
    });

    setSelectedFilter({ ...selectedFilter, options: updatedSelectedOptions });
    setFilters(updatedFilters);
    props.onSave(updatedFilters);
    setSearchValue('');
  };

  const clearSelectedFilterOptions = () => {
    const updatedSelectedOptions = selectedFilter.options.map((option) => {
      return { ...option, selected: false };
    });

    const updatedFilters = filters.map((filter) => {
      if (filter.type === selectedFilter.type) {
        return { ...filter, options: updatedSelectedOptions };
      }
      return filter;
    });

    setSelectedFilter({ ...selectedFilter, options: updatedSelectedOptions });
    setFilters(updatedFilters);
    props.onSave(updatedFilters);
    setSearchValue('');
  };

  const selectFilter = (filter: Filter) => {
    setSelectedFilter(filter);
    setSearchValue('');
  };

  const onSelectedOption = (option: FilterOption, isSelected: boolean) => {
    const isMeOption = option.name === 'Me';
    // #2610: never mutate `selectedFilter` — the saved view and the workspace query
    // cache can hold the very same Filter objects.
    const updated: Filter = {
      ...selectedFilter,
      options: selectedFilter.options.map((currOption) => {
        if (isMeOption && currOption.name === 'Me') {
          return {
            ...currOption,
            selected: !currOption.selected,
          };
        } else if (
          !isMeOption && currOption.metadata?.id && option.metadata?.id
            ? currOption.metadata.id === option.metadata.id
            : currOption.name === option.name
        ) {
          return {
            ...currOption,
            selected: isSelected,
          };
        }
        return currOption;
      }),
    };

    const updatedFilters = filters.map((filter) => {
      if (filter.type === updated.type) {
        return updated;
      }
      return filter;
    });

    setSelectedFilter(updated);
    setFilters(updatedFilters);
    props.onSave(updatedFilters);
  };

  const onSelectedOptions = (options: FilterOption[], isSelected: boolean) => {
    const optionMap = new Map<string | number, FilterOption>();
    options.forEach((option) => {
      const key = option.metadata?.id || option.name;
      optionMap.set(key, option);
    });

    const updated: Filter = {
      ...selectedFilter,
      options: selectedFilter.options.map((currOption) => {
        const key = currOption.metadata?.id || currOption.name;
        const matchingOption = optionMap.get(key);

        if (matchingOption) {
          return {
            ...currOption,
            selected: isSelected,
          };
        }
        return currOption;
      }),
    };

    const updatedFilters = filters.map((filter) => {
      if (filter.type === updated.type) {
        return updated;
      }
      return filter;
    });

    setSelectedFilter(updated);
    setFilters(updatedFilters);
    props.onSave(updatedFilters);
  };

  const removeFilter = (filter: Filter) => {
    if (selectedFilter.type === filter.type) {
      setSelectedFilter({
        ...selectedFilter,
        options: selectedFilter.options.map((currOption) => {
          return {
            ...currOption,
            selected: false,
          };
        }),
      });
    }

    const updatedFilters = filters.map((currFilter) => {
      if (filter.type === currFilter.type) {
        return {
          ...currFilter,
          options: currFilter.options.map((option) => {
            return {
              ...option,
              selected: false,
            };
          }),
        };
      }
      return currFilter;
    });
    setFilters(updatedFilters);
    props.onSave(updatedFilters);
  };

  return (
    <>
      <ConfirmSaveForEveryoneModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmSave}
        onCancel={handleCancelConfirm}
        mode="filter"
      />
      <Modal
        title="Filters"
        open={props.isOpen}
        onCancel={handleCancel}
        onClose={handleCancel}
        footer={null}
        width={640}
        centered
      >
        <div className="flex h-[340px]">
          <div className="min-h-0 overflow-y-auto" style={{ flex: 1, marginRight: '16px' }}>
            {filters.map((filter) => (
              <div
                className={
                  selectedFilter.type === filter.type
                    ? 'flex items-center bg-taskonioGreen-100 text-brand-grey-dark text-sm font-normal px-2 py-1 my-0.5 cursor-pointer rounded border-box border border-solid border-taskonioGreen-200'
                    : // transparent border reserves the selected state's 1px border so the
                      // row doesn't grow / the title doesn't shift when switching categories (#559)
                      'flex items-center text-neutral-900 text-sm font-normal px-2 py-1 my-0.5 cursor-pointer hover:bg-neutral-200 rounded border border-solid border-transparent'
                }
                key={filter.name}
                onClick={() => selectFilter(filter)}
              >
                {mapFilterOptionName(filter.name)}
                {filter.options.filter((option) => option.selected).length
                  ? ` (${filter.options.filter((option) => option.selected).length})`
                  : ''}
              </div>
            ))}
          </div>
          <div className="w-px min-h-full bg-neutral-300 rounded-sm" />
          <div className="mx-4 flex flex-col gap-1 min-h-0 overflow-y-auto" style={{ flex: 1.4 }}>
            <div className="flex items-center justify-between">
              <div className="text-neutral-900 text-sm font-medium leading-tight mb-2">Filter by</div>
              {selectedFilter.options.filter((option) => option.selected).length > 0 && (
                <BaseButton type="secondary" size="small" onClick={clearSelectedFilterOptions}>
                  Clear
                </BaseButton>
              )}
            </div>
            {(selectedFilter.type === 'owner' || selectedFilter.type === 'epic') && (
              <Input
                prefix={<SearchOutlined className="text-neutral-900" />}
                placeholder={selectedFilter.type === 'owner' ? 'Search for owner' : 'Search for epic'}
                value={searchValue}
                onChange={onSearchInput}
                className="mb-1"
              />
            )}
            {groupedFilterOptions &&
              Object.keys(groupedFilterOptions).map((group) => (
                <FilterGroupOption
                  key={group}
                  name={group}
                  options={groupedFilterOptions[group]}
                  onSelectedGroup={onSelectedOptions}
                  onSelectedOption={onSelectedOption}
                />
              ))}
            {!groupedFilterOptions &&
              selectedFilterSearchedOptions.map((option) => (
                <FilterOption key={option.metadata?.id || option.name} option={option} onSelected={onSelectedOption} />
              ))}
            {!groupedFilterOptions && !selectedFilterSearchedOptions.length && (
              <div className="h-full flex flex-col justify-center items-center gap-5">
                <div className="w-[46px] h-[46px] bg-neutral-300 rounded-full flex items-center justify-center">
                  <FilterOutlined />
                </div>
                <div className="text-center text-neutral-900 text-sm font-medium font-['Figtree'] leading-tight">
                  No results found
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="h-px bg-neutral-300 rounded-sm mb-3" />

        <div className="h-[26px] flex items-center justify-between mr-4">
          <div className="flex items-center gap-2">
            <div className="text-neutral-900 text-sm font-medium leading-tight">Selected Filters</div>
            <div className="bg-purple-100 text-neutral-900 px-1 py-0.5 rounded text-sm font-medium leading-tight">
              {selectedFilters.length}
            </div>
          </div>
          {selectedFilters.length > 0 && (
            <BaseButton type="secondary" size="small" onClick={clearAllFilters}>
              Clear All
            </BaseButton>
          )}
        </div>

        {selectedFilters.length > 0 && (
          <div className="flex items-center gap-2 mt-1 pb-2 overflow-x-auto">
            {selectedFilters.map((filter) => (
              <SelectedFilter key={filter.type} filter={filter} onRemove={removeFilter} />
            ))}
          </div>
        )}
        {selectedFilters.length === 0 && <div className="h-5 mt-1 pb-2"></div>}
        {!props.isViewSelectedFilters && (
          <div className="flex justify-end gap-2">
            <BaseButton
              size="medium"
              type="text"
              onClick={() => {
                clearAllFilters();
                props.resetToSavedFilters();
              }}
            >
              Reset
            </BaseButton>

            <BaseButton size="medium" type="orange" onClick={handleSaveForAllClick}>
              <CheckOutlined />
              Save for everyone
            </BaseButton>
          </div>
        )}
        {props.isViewSelectedFilters && <div className="h-[22px] mt-1 pb-2"></div>}
      </Modal>
    </>
  );
};

export type FilterOption = {
  name: string;
  selected: boolean;
} & (
  | { type: 'taskType'; metadata: TaskType }
  | { type: 'sprintStatus'; metadata: string }
  | { type: 'epic'; metadata: Epic }
  | { type: 'status'; metadata: TaskStatus }
  | { type: 'priority'; metadata: PriorityOptions }
  | { type: 'owner'; metadata: User | null }
  | { type: 'estimate'; metadata: number | null }
  | { type: 'startDate'; metadata: string }
  | { type: 'deadline'; metadata: string }
  | { type: 'labels'; metadata: SpaceLabel | null }
);

interface FilterOptionProps {
  option: FilterOption;
  onSelected: (option: FilterOption, isSelected: boolean) => void;
}

const FilterOption = (props: FilterOptionProps) => {
  const { type, name, selected, metadata } = props.option;

  const onClick = () => {
    props.onSelected(props.option, !selected);
  };

  let priorityComponent = <div />;
  if (type === 'priority') {
    const { colorFill, label } = PriorityFlagPropsMap[metadata];
    priorityComponent = (
      <div className="flex items-center ml-1 h-7">
        {metadata === PriorityOptions.NoPriority ? (
          <FlagPriorityWhite style={{ width: flagWidth }} />
        ) : (
          <FlagPriority style={{ width: flagWidth }} fill={colorFill} />
        )}
        <span style={{ color: colorFill }}>{label}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        borderColor: selected ? colorTokens.blue[300] : colorTokens.neutral[300],
      }}
      className="flex items-center justify-start gap-2 py-1 px-2 rounded text-sm font-normal cursor-pointer rounded border border-solid"
      onClick={onClick}
    >
      {!selected && <div className="w-[14px] h-[14px] rounded-sm border-box border border-solid border-neutral-600" />}
      {selected && <CheckSquareFilled style={{ fontSize: '16px' }} className="text-blue-500" />}
      {type === 'status' && (
        <StatusLabel
          group={metadata.group}
          title={metadata.title}
          color={metadata.color}
          component={StatusComponentType.EditStatusesModal}
        />
      )}
      {type === 'priority' && priorityComponent}
      {type === 'taskType' && <TaskTypeLabel type={metadata} size="medium" />}
      {type === 'owner' && (
        <UserAvatar assignee={metadata?.id} usersWithAccessToSpace={[{ User: metadata, userId: metadata?.id }]} />
      )}
      {type === 'sprintStatus' && (
        <SprintStatusLabel sprintStatus={metadata as 'planning' | 'sprint' | 'kanban' | 'none'} component="dropdown" />
      )}
      {type === 'labels' && metadata && <LabelChip label={metadata} type={LabelChipType.Modal} />}
      {type === 'labels' && !metadata && (
        <div className="justify-start text-brand-grey-dark text-sm font-normal leading-tight pl-1">No Label</div>
      )}
      {type !== 'status' &&
        type !== 'priority' &&
        type !== 'taskType' &&
        type !== 'sprintStatus' &&
        type !== 'labels' && (
          <div className="h-7 max-w-[250px] truncate text-brand-grey-dark text-sm font-normal leading-7">{name}</div>
        )}
    </div>
  );
};

interface FilterGroupOptionProps {
  name: string;
  options: FilterOption[];
  onSelectedGroup: (options: FilterOption[], isSelected: boolean) => void;
  onSelectedOption: (option: FilterOption, isSelected: boolean) => void;
}

const FilterGroupOption = (props: FilterGroupOptionProps) => {
  const { name, options, onSelectedGroup, onSelectedOption } = props;

  const groupSelected = options.filter((option) => option.selected).length === options.length;

  const onClick = () => {
    onSelectedGroup(options, !groupSelected);
  };

  return (
    <div className="flex-col items-center justify-start mb-5" key={name}>
      <div className="flex gap-1 text-neutral-900 text-xs font-semibold mb-3 cursor-pointer" onClick={onClick}>
        {!groupSelected && (
          <div className="w-[14px] h-[14px] rounded-sm border-box border border-solid border-neutral-600" />
        )}
        {groupSelected && <CheckSquareFilled style={{ fontSize: '16px' }} className="text-blue-500" />}
        {name}
      </div>
      {options.map((option, idx) => (
        <div className="mt-1" key={idx}>
          <FilterOption key={option.metadata?.id || option.name} option={option} onSelected={onSelectedOption} />
        </div>
      ))}
    </div>
  );
};

const SelectedFilter = ({ filter, onRemove }: { filter: Filter; onRemove: (filter: Filter) => void }) => {
  const selectedOptionsStr = filter.options
    .filter((option) => option.selected)
    .map((option) => capitalizeFirstLetter(option.name))
    .join(', ');

  return (
    <div className="px-2 py-1 bg-purple-100 rounded justify-center items-center gap-1 flex">
      <div className="max-w-[140px] text-purple-600 text-xs font-normal leading-none whitespace-nowrap overflow-hidden text-ellipsis">
        {filter.name}: {selectedOptionsStr}
      </div>
      <CloseOutlined className="w-3 h-3 relative text-purple-600 cursor-pointer" onClick={() => onRemove(filter)} />
    </div>
  );
};

function groupFilterOptions(filter: Filter): Record<string, FilterOption[]> | null {
  if (filter.type !== 'status') {
    return null;
  }

  const groupedOptions = filter.options.reduce<Record<string, FilterOption[]>>(
    (acc, option) => {
      if (option.type === 'status') {
        const group = TaskStatusGroupUtils.getTitle(option.metadata.group);

        if (!acc[group]) {
          acc[group] = [];
        }

        acc[group].push(option);
      }
      return acc;
    },
    {
      [TaskStatusGroupUtils.getTitle(TaskStatusGroup.notStarted)]: [],
      [TaskStatusGroupUtils.getTitle(TaskStatusGroup.inProgress)]: [],
      [TaskStatusGroupUtils.getTitle(TaskStatusGroup.completed)]: [],
    }
  );

  return groupedOptions;
}

function mapFilterOptionName(name: string) {
  if (name === 'Deadline') {
    return 'Target Date';
  }
  return name;
}

export default TaskFiltersModal;
